import { datadogLogs } from '@datadog/browser-logs';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { isAxiosError } from 'src/api/instance';
import Modal, { ModalVariant } from 'src/components/organisms/Modal';
import { actions as proofsActions, RESULT_INSUFFICIENT_REASON_LABEL } from 'src/features/proofs';
import proofApi from 'src/features/proofs/api';
import {
  OFFER_LETTER_CHECKLIST_STEPS,
  OLV_REQUIRED_FIELDS,
  type OlvFieldKey
} from 'src/features/proofs/offerLetterChecklistConfig';
import { getProofsByEntryIdThunk, updateProofThunk } from 'src/features/proofs/services';
import useSnackbarProvider from 'src/hooks/useSnackbarProvider';
import {
  CompensationData,
  ReviewContact,
  VerificationContact,
  VerificationFormData
} from 'src/features/proofs/ts/interfaces/OfferLetterVerification';
import { useAppDispatch } from 'src/store';
import { EntryResultEnum, ProofResultInsufficientReasonEnum } from 'src/ts/enums';
import { IProof, ProofResultInsufficientReasonType } from 'src/ts/interfaces';
import { isE164Phone } from '../helpers/contactPhone';
import { FormValues } from '../helpers/proofHelpers';
import {
  clearPersistedState,
  ProcessStatus,
  useOfferLetterChecklist
} from '../hooks/useOfferLetterChecklist';
import { OlvLockState } from '../hooks/useOlvLockState';
import {
  ErrorValidationMessage,
  OfferLetterCheckItemInfo,
  OfferLetterCheckItemLabel,
  OfferLetterCheckItemRow,
  OfferLetterLlmField,
  OfferLetterLlmInput,
  OfferLetterPanel,
  OfferLetterPanelContent,
  OfferLetterPanelHeader,
  OfferLetterPanelTitle,
  OfferLetterStepContainer
} from '../styles';
import ContactReviewSection from './ContactReviewSection';
import OfferLetterActionBar from './OfferLetterActionBar';
import { ActionBarContainer, ActionButton } from './OfferLetterActionBar/styles';
import OfferLetterCheckItem from './OfferLetterCheckItem';
import OlvLockOverlay from './OlvLockOverlay';

interface OfferLetterVerificationPanelProps {
  currentProof: IProof | undefined;
  onNextDocument: () => void;
  onReviewReport: () => void;
  isLastDocument: boolean;
  onUpdateLocalFormData: (
    updater: (prev: Record<string, FormValues>) => Record<string, FormValues>
  ) => void;
  lockState: OlvLockState;
}

const CONFIRM_ACTIONS = {
  EDITED: 'edited',
  INSUFFICIENT: 'insufficient'
} as const;

type ConfirmAction = (typeof CONFIRM_ACTIONS)[keyof typeof CONFIRM_ACTIONS] | null;

// Submitted-summary labels, keyed to OLV_LLM_FIELD_MAP: a field added to the
// map fails to compile here until it gets a label, so it can't silently drop
// out of the summary.
const OLV_SUMMARY_LABELS = {
  company_name: 'Company',
  employee_name: 'Employee',
  employee_title: 'Title',
  start_date: 'Start Date',
  website: 'Website',
  address: 'Address'
} as const satisfies Record<OlvFieldKey, string>;

const BONUS_LABELS: Record<string, string> = {
  signing_bonus: 'Signing Bonus',
  annual_bonus: 'Annual Bonus',
  commissions: 'Commissions'
};

// ITL-2880: guard with `typeof !== 'number'`, not `!isFinite` — `isFinite(null)` is true,
// so a schema-nullable null amount would slip through and crash `.toLocaleString()`.
export const formatCompensationDisplay = (comp: CompensationData | null | undefined): string => {
  if (!comp || typeof comp.amount !== 'number' || !isFinite(comp.amount)) return '';
  let display = '';
  // 'hourly' renders the per-hour form; every other (salary) cadence renders the
  // amount, omitting the `/period` suffix when the cadence is unset (null) —
  // never render "$5,000/null".
  if (comp.period === 'hourly') {
    const base = `$${comp.amount}/hour`;
    display = comp.hours_per_week ? `${base} (${comp.hours_per_week} hrs/wk)` : base;
  } else {
    display = comp.period
      ? `$${comp.amount.toLocaleString()}/${comp.period}`
      : `$${comp.amount.toLocaleString()}`;
  }
  if (comp.bonuses?.length) {
    const bonusStr = comp.bonuses
      .filter((b) => typeof b.amount === 'number' && isFinite(b.amount))
      .map((b) => `${BONUS_LABELS[b.type] || b.type}: $${b.amount.toLocaleString()}`)
      .join(', ');
    if (bonusStr) display += ` + ${bonusStr}`;
  }
  if (comp.other_compensation) display += ` + ${comp.other_compensation}`;
  return display;
};

// ITL-2932: `hours_per_week` is only meaningful for an hourly period. The UI
// intentionally retains it across a period switch (so an analyst can toggle
// hourly → salary → hourly without losing the value), so strip it at submit
// time when the period is no longer hourly — otherwise a stale hours_per_week
// rides along in the saved payload.
export const compensationForSubmit = (
  comp: CompensationData | null | undefined
): CompensationData | undefined => {
  if (!comp) return undefined;
  if (comp.period !== 'hourly' && comp.hours_per_week !== undefined) {
    const { hours_per_week, ...rest } = comp;
    return rest;
  }
  return comp;
};

// ITL-2766: shared ReviewContact → VerificationContact mapper. Three call
// sites (SFDC submit, INSUFFICIENT submit, SFDC preview-modal display)
// previously had their own inline mappers; an inconsistency between the
// SFDC submit and preview filters was the root cause of the "7 contacts
// shown, 2 reached SF" UAT report. Centralising prevents that class of
// drift. Behavior:
//   - whitespace-only or null name → UNNAMED_CONTACT_PLACEHOLDER, so all
//     reviewed contacts reach SF; reps still ID them via role/email/phone
//   - phone empty string / whitespace / null → omitted from payload
//     (matches how other optional strings on the same payload are handled)
// ITL-2807: emits the AI-pipeline shape directly (email array / phone scalar
// / type) — no longer down-converts to the legacy emails/phones/role keys.
const UNNAMED_CONTACT_PLACEHOLDER = 'Unknown Contact';
const OLV_EMAIL_REGEX = /^[^\s]+@[^\s]+$/;
const SUBMIT_ERROR_SNACKBAR_KEY = 'olv-submit-error';

export const toVerificationContact = (c: ReviewContact): VerificationContact => ({
  name: c.name?.trim() || UNNAMED_CONTACT_PLACEHOLDER,
  // ITL-2880: the sessionStorage RESTORE path bypasses the seed strip — filter
  // nulls here so they can't reach the Salesforce payload.
  email: (c.email ?? []).filter((e) => e != null),
  source: c.source?.trim() || 'analyst',
  type: c.type ?? undefined,
  phone: c.phone?.trim() || undefined
});

const OfferLetterVerificationPanel: FC<OfferLetterVerificationPanelProps> = ({
  currentProof,
  onNextDocument,
  onReviewReport,
  isLastDocument,
  onUpdateLocalFormData,
  lockState
}) => {
  const dispatch = useAppDispatch();
  const { showSnackbar, closeSnackbar, VariantType } = useSnackbarProvider();
  const {
    checks,
    fieldValues,
    compensationData,
    contactsData,
    setCheck,
    setFieldValue,
    setContacts,
    processStatus,
    isReadyToSubmit,
    aiBlankFields,
    isAnalystSubmitted
  } = useOfferLetterChecklist(currentProof);

  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [selectedReasonCodes, setSelectedReasonCodes] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // ITL-3094: analyst chose "Edit verification" on a submitted proof — show the
  // normal form instead of the submitted view until they leave the proof.
  const [isEditing, setIsEditing] = useState(false);

  // Deliberately keyed to proof changes only: a resubmit from the edit form
  // stays on the form rather than returning to the summary — that return flow
  // is ITL-3088's resubmit design.
  useEffect(() => {
    setIsEditing(false);
  }, [currentProof?.id]);

  // The verification fetch has settled — until then, contact-derived hard/soft
  // codes, the required-inputs block, and the contacts section must not fire
  // off unseeded state (editing contacts pre-seed would make SEED_CONTACTS
  // no-op and silently discard the AI-extracted contacts).
  const seedSettled = aiBlankFields !== null;

  // ITL-3021: required fields render as inputs only when the AI extraction left
  // them blank (keyed off the seed, not current value, so the input doesn't
  // vanish as the analyst types).
  const { blankRequiredFields, requiredFieldsComplete } = useMemo(
    () => ({
      blankRequiredFields: OLV_REQUIRED_FIELDS.filter((f) => (aiBlankFields ?? []).includes(f.key)),
      requiredFieldsComplete:
        seedSettled && OLV_REQUIRED_FIELDS.every((f) => !!fieldValues[f.key]?.trim())
    }),
    [aiBlankFields, seedSettled, fieldValues]
  );

  const handleMarkEdited = () => {
    setErrorMessage(null);
    setConfirmAction(CONFIRM_ACTIONS.EDITED);
  };

  const handleMarkInsufficient = (reasonCodes: string[]) => {
    setErrorMessage(null);
    setSelectedReasonCodes(reasonCodes);
    setConfirmAction(CONFIRM_ACTIONS.INSUFFICIENT);
  };

  // ITL-2894: verify/PASS submits directly — no confirm/preview popup. The SFDC
  // modal was vestigial post-ITL-2809 (the SF case is now created at the
  // leasing-agent step) and preview-only: every submitted field comes from the
  // form, so submitting directly loses no analyst input.
  const submitVerification = async () => {
    if (!currentProof) return;
    closeSnackbar(SUBMIT_ERROR_SNACKBAR_KEY);
    setIsSubmitting(true);

    try {
      const payload: VerificationFormData = {
        proof_id: currentProof.id,
        employer_name: fieldValues['company_name'] || '',
        employee_name: fieldValues['employee_name'] || '',
        employer_address: fieldValues['address'] || undefined,
        employer_website: fieldValues['website'] || undefined,
        employee_title: fieldValues['employee_title'] || undefined,
        start_date: fieldValues['start_date'] || undefined,
        compensation: compensationForSubmit(compensationData),
        contacts: contactsData?.map(toVerificationContact) ?? [],
        analyst_notes: undefined
      };

      await proofApi.submitVerification(payload);

      // ITL-2894: the direct-submit path (SFDC modal removed) had no telemetry —
      // log success/failure so this failure mode is visible despite the fraud-web
      // RUM gap. Never log error.response.data (may carry employer PII).
      datadogLogs.logger.info('OLV verification submitted', {
        proof_id: currentProof.id,
        entry_id: currentProof.entry_id
      });

      // ITL-3094: the draft is consumed; a stale RESTORE would otherwise win
      // over the analyst-row seed on the next revisit.
      clearPersistedState(currentProof.id);

      dispatch(
        proofsActions.updateLocalProofState({
          id: currentProof.id,
          formValues: {
            result: EntryResultEnum.Undetermined,
            resultInsufficientReason: ProofResultInsufficientReasonEnum.CUSTOM,
            olvReasons: ['PENDING_EMPLOYER_VERIFICATION'] as ProofResultInsufficientReasonType[],
            note: ''
          }
        })
      );
      onUpdateLocalFormData((prev) => ({
        ...prev,
        [currentProof.id]: {
          result: 'UNDETERMINED',
          reason: 'PENDING_EMPLOYER_VERIFICATION',
          documentType: currentProof.type || '',
          olvReasons: ['PENDING_EMPLOYER_VERIFICATION']
        }
      }));

      if (isLastDocument) {
        onReviewReport();
      } else {
        onNextDocument();
      }
    } catch (error) {
      datadogLogs.logger.error('OLV verification submit failed', {
        proof_id: currentProof.id,
        entry_id: currentProof.entry_id,
        status: isAxiosError(error) ? error.response?.status : undefined
      });

      const entries =
        isAxiosError(error) && error.response?.data?.errors
          ? Object.entries(error.response.data.errors as Record<string, string[]>)
          : [];
      showSnackbar(
        VariantType.error,
        entries.length > 0
          ? entries.map(([field, msgs]) => `${field}: ${msgs.join(', ')}`).join('; ')
          : 'Failed to submit verification. Please try again.',
        undefined,
        undefined,
        { key: SUBMIT_ERROR_SNACKBAR_KEY, autoHideDuration: 10000 }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    if (!currentProof) return;
    setIsSubmitting(true);

    try {
      if (confirmAction === CONFIRM_ACTIONS.EDITED) {
        await dispatch(
          updateProofThunk({
            id: currentProof.id,
            result: EntryResultEnum.Edited,
            type: currentProof.type
          })
        ).unwrap();
        onUpdateLocalFormData((prev) => ({
          ...prev,
          [currentProof.id]: {
            result: 'EDITED',
            reason: 'NONE',
            documentType: currentProof.type || '',
            olvReasons: []
          }
        }));
      } else if (confirmAction === CONFIRM_ACTIONS.INSUFFICIENT) {
        await proofApi.submitInsufficient({
          proof_id: currentProof.id,
          reason_codes: selectedReasonCodes,
          employer_name: fieldValues['company_name'] || '',
          employee_name: fieldValues['employee_name'] || '',
          employer_address: fieldValues['address'] || undefined,
          employer_website: fieldValues['website'] || undefined,
          employee_title: fieldValues['employee_title'] || undefined,
          start_date: fieldValues['start_date'] || undefined,
          compensation: compensationForSubmit(compensationData),
          contacts: contactsData?.map(toVerificationContact) ?? []
        });
        dispatch(
          proofsActions.updateLocalProofState({
            id: currentProof.id,
            formValues: {
              result: EntryResultEnum.Undetermined,
              resultInsufficientReason: ProofResultInsufficientReasonEnum.CUSTOM,
              olvReasons: selectedReasonCodes as ProofResultInsufficientReasonType[],
              note: ''
            }
          })
        );
        onUpdateLocalFormData((prev) => ({
          ...prev,
          [currentProof.id]: {
            result: 'UNDETERMINED',
            reason: 'CUSTOM',
            documentType: currentProof.type || '',
            olvReasons: selectedReasonCodes
          }
        }));
        clearPersistedState(currentProof.id);
        // ITL-2769 (approach B): server may have cascaded sibling OLs on
        // this entry to UNDETERMINED + CUSTOM. Refetch entry proofs so
        // Redux reflects the cascade-target state — without this, the
        // sibling OL's panel stays interactive and the cascade lock
        // overlay can't fire. The above updateLocalProofState dispatch
        // covers ONLY the originating proof; the refetch covers all.
        //
        // .unwrap().catch instrumentation: if the refetch fails (transient
        // 5xx, network), the sibling cascade-target OL would stay `unlocked`
        // in Redux indefinitely with no log line. Emit a Datadog warn so the
        // stale Redux view is detectable from telemetry.
        if (currentProof.entry_id) {
          const refetchEntryId = currentProof.entry_id;
          dispatch(getProofsByEntryIdThunk(refetchEntryId))
            .unwrap()
            .catch((error: unknown) => {
              datadogLogs.logger.warn('Cascade refetch after mark_insufficient failed', {
                entry_id: refetchEntryId,
                proof_id: currentProof.id,
                error: String(error)
              });
            });
        }
      }

      setConfirmAction(null);
      if (isLastDocument) {
        onReviewReport();
      } else {
        onNextDocument();
      }
    } catch (error) {
      if (
        confirmAction === CONFIRM_ACTIONS.INSUFFICIENT &&
        isAxiosError(error) &&
        error.response?.data?.errors
      ) {
        const entries = Object.entries(error.response.data.errors as Record<string, string[]>);
        if (entries.length > 0) {
          setErrorMessage(
            entries.map(([field, msgs]) => `${field}: ${msgs.join(', ')}`).join('; ')
          );
        } else {
          setErrorMessage('Failed to mark as insufficient. Please try again.');
        }
      } else {
        setErrorMessage(
          confirmAction === CONFIRM_ACTIONS.INSUFFICIENT
            ? 'Failed to mark as insufficient. Please try again.'
            : 'Failed to update document. Please try again.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalTitle =
    confirmAction === CONFIRM_ACTIONS.EDITED ? 'Mark as Failed' : 'Mark Insufficient';

  const modalLabel =
    confirmAction === CONFIRM_ACTIONS.EDITED ? 'Yes, mark as Failed' : 'Yes, mark Insufficient';

  const modalVariant =
    confirmAction === CONFIRM_ACTIONS.EDITED ? ModalVariant.danger : ModalVariant.none;

  const modalText =
    confirmAction === CONFIRM_ACTIONS.EDITED
      ? 'Are you sure you want to mark this document as Failed?'
      : confirmAction === CONFIRM_ACTIONS.INSUFFICIENT
      ? `Reason codes: ${selectedReasonCodes
          .map(
            (r) =>
              RESULT_INSUFFICIENT_REASON_LABEL[
                r as keyof typeof RESULT_INSUFFICIENT_REASON_LABEL
              ] || r
          )
          .join(', ')}`
      : undefined;

  const textWithError = modalText
    ? [modalText, errorMessage].filter(Boolean).join('\n\n')
    : undefined;

  // Contact review affects process status and reason codes
  const hasNoContacts = seedSettled && (!contactsData || contactsData.length === 0);
  const hasUnverifiedContacts =
    seedSettled &&
    contactsData &&
    contactsData.length > 0 &&
    !contactsData.some((c) => c.is_verified);

  const contactReviewProcessStatus: ProcessStatus = hasNoContacts
    ? 'stopped_insufficient'
    : processStatus;

  const contactReviewHardCodes = hasNoContacts ? ['NO_CONTACTS_AVAILABLE'] : [];

  const contactReviewSoftCodes = hasUnverifiedContacts ? ['NO_VERIFIED_CONTACTS'] : [];

  const allFieldChecksComplete =
    !contactsData ||
    contactsData.length === 0 ||
    contactsData.every((c) => {
      const fc = c.field_checks;
      if (!fc) return false;
      const emailsComplete =
        Array.isArray(fc.email) && (fc.email.length === 0 || fc.email.every((e) => e !== 'unset'));
      return fc.name !== 'unset' && fc.type !== 'unset' && emailsComplete && fc.phone !== 'unset';
    });

  // ITL-2807: value-level guard mirroring the API's contact rules — each
  // contact needs >=1 email or a phone, and any phone present must be valid.
  // Names the offending contact(s); folded into the submit-enable chain below.
  const contactValidationMessage = useMemo(() => {
    if (!contactsData || contactsData.length === 0) return null;

    const missingContactInfo = contactsData
      .map((c, i) => ({ c, i }))
      .filter(({ c }) => !(c.email ?? []).some((e) => OLV_EMAIL_REGEX.test(e)) && !c.phone?.trim())
      .map(({ i }) => `Contact ${i + 1}`);

    if (missingContactInfo.length > 0) {
      return `Each contact needs at least one valid email or a phone: ${missingContactInfo.join(
        ', '
      )}.`;
    }

    const invalidPhone = contactsData
      .map((c, i) => ({ c, i }))
      .filter(({ c }) => !!c.phone?.trim() && !isE164Phone(c.phone))
      .map(({ i }) => `Contact ${i + 1}`);

    if (invalidPhone.length > 0) {
      return `Enter a valid phone number for: ${invalidPhone.join(', ')}.`;
    }

    return null;
  }, [contactsData]);

  // ITL-2807: the contact guard hides the submit path with no API call, so the
  // backend `invalid_olv_contact` canary never sees these. Emit a browser log
  // when the guard engages so over-triggering is visible in Datadog post-deploy.
  // The message names only "Contact N" — no contact PII.
  //
  // Guard-firing is an expected, common state (any incomplete AI-seeded contact
  // trips it), and `contactValidationMessage` recomputes a fresh string on every
  // contact edit — so log at most once per distinct guard message and reset when
  // the guard clears, rather than re-firing on each keystroke.
  const lastLoggedGuardMessage = useRef<string | null>(null);
  useEffect(() => {
    if (!contactValidationMessage) {
      lastLoggedGuardMessage.current = null;
      return;
    }
    if (currentProof?.id && contactValidationMessage !== lastLoggedGuardMessage.current) {
      lastLoggedGuardMessage.current = contactValidationMessage;
      datadogLogs.logger.warn('OLV contact guard blocked submit', {
        event: 'offer_letter.contacts.guard_blocked',
        proof_id: currentProof.id,
        entry_id: currentProof.entry_id,
        reason: contactValidationMessage
      });
    }
  }, [contactValidationMessage, currentProof?.id, currentProof?.entry_id]);

  return (
    <OfferLetterPanel
      data-testid="offer-letter-verification-panel"
      data-lock-state={lockState}
      data-proof-id={currentProof?.id}
    >
      <OfferLetterPanelHeader>
        <OfferLetterPanelTitle>Offer Letter Verification</OfferLetterPanelTitle>
      </OfferLetterPanelHeader>

      {lockState !== 'unlocked' ? (
        <OlvLockOverlay lockState={lockState} />
      ) : isAnalystSubmitted && !isEditing ? (
        <OfferLetterPanelContent>
          <OfferLetterStepContainer $reviewed data-testid="olv-submitted-state">
            <OfferLetterCheckItemRow>
              <OfferLetterCheckItemInfo>
                <OfferLetterCheckItemLabel>Verification Submitted</OfferLetterCheckItemLabel>
              </OfferLetterCheckItemInfo>
            </OfferLetterCheckItemRow>
            <div>This offer letter was verified by an analyst.</div>
            <div>
              {[
                ...(Object.keys(OLV_SUMMARY_LABELS) as OlvFieldKey[]).map(
                  (key): readonly [string, string | undefined] => [
                    OLV_SUMMARY_LABELS[key],
                    fieldValues[key]
                  ]
                ),
                ['Compensation', formatCompensationDisplay(compensationData)] as const,
                ['Contacts', contactsData?.length ? String(contactsData.length) : ''] as const
              ]
                .filter(([, value]) => !!value)
                .map(([label, value]) => (
                  <OfferLetterCheckItemRow key={label}>
                    <OfferLetterCheckItemInfo>
                      <OfferLetterCheckItemLabel>
                        {label}: {value}
                      </OfferLetterCheckItemLabel>
                    </OfferLetterCheckItemInfo>
                  </OfferLetterCheckItemRow>
                ))}
            </div>
            <ActionButton
              type="button"
              data-testid="olv-edit-verification"
              onClick={() => {
                // ITL-3094: edit-rate on submitted proofs is the signal that
                // informs ITL-3088's resubmit design — no other telemetry sees it.
                if (currentProof?.id) {
                  datadogLogs.logger.info('OLV edit verification opened', {
                    event: 'offer_letter.verification.edit_opened',
                    proof_id: currentProof.id,
                    entry_id: currentProof.entry_id
                  });
                }
                setIsEditing(true);
              }}
            >
              Edit Verification
            </ActionButton>
          </OfferLetterStepContainer>
        </OfferLetterPanelContent>
      ) : (
        <>
          <OfferLetterPanelContent>
            {OFFER_LETTER_CHECKLIST_STEPS.map((step) => (
              <OfferLetterStepContainer
                key={step.id}
                $reviewed={checks[step.id] === 'pass' || checks[step.id] === 'fail'}
                data-testid={`olv-step-${step.id}`}
              >
                <OfferLetterCheckItem
                  item={step}
                  status={checks[step.id] || 'unset'}
                  onSetCheck={(value) => setCheck(step.id, value)}
                />
              </OfferLetterStepContainer>
            ))}

            {!seedSettled && (
              <OfferLetterStepContainer $reviewed data-testid="olv-seed-loading">
                Loading extracted offer letter data…
              </OfferLetterStepContainer>
            )}

            {blankRequiredFields.length > 0 && (
              <div data-testid="olv-required-fields">
                {blankRequiredFields.map((field) => (
                  <OfferLetterStepContainer
                    key={field.key}
                    $reviewed={!!fieldValues[field.key]?.trim()}
                    data-testid={`olv-required-field-row-${field.key}`}
                  >
                    <OfferLetterCheckItemRow>
                      <OfferLetterCheckItemInfo>
                        <OfferLetterCheckItemLabel>
                          <label htmlFor={`olv-required-field-${field.key}`}>
                            {field.label} (required)
                          </label>
                        </OfferLetterCheckItemLabel>
                      </OfferLetterCheckItemInfo>
                    </OfferLetterCheckItemRow>
                    <OfferLetterLlmField>
                      <OfferLetterLlmInput
                        id={`olv-required-field-${field.key}`}
                        type="text"
                        value={fieldValues[field.key] ?? ''}
                        onChange={(e) => setFieldValue(field.key, e.target.value)}
                        placeholder="Enter Value"
                        aria-required="true"
                        aria-invalid={!fieldValues[field.key]?.trim()}
                        data-testid={`olv-required-field-${field.key}`}
                      />
                    </OfferLetterLlmField>
                  </OfferLetterStepContainer>
                ))}
              </div>
            )}

            {seedSettled && !requiredFieldsComplete && (
              <ErrorValidationMessage data-testid="olv-required-fields-warning" role="alert">
                Required before submitting:{' '}
                {OLV_REQUIRED_FIELDS.filter((f) => !fieldValues[f.key]?.trim())
                  .map((f) => f.label)
                  .join(', ')}
              </ErrorValidationMessage>
            )}

            {seedSettled && (
              <ContactReviewSection
                contacts={contactsData}
                fieldValues={fieldValues}
                onContactsChange={setContacts}
              />
            )}
          </OfferLetterPanelContent>

          {contactValidationMessage && contactReviewProcessStatus !== 'stopped_edited' && (
            <ActionBarContainer>
              <ErrorValidationMessage data-testid="olv-contact-info-warning">
                {contactValidationMessage}
              </ErrorValidationMessage>
            </ActionBarContainer>
          )}

          <OfferLetterActionBar
            processStatus={contactReviewProcessStatus}
            autoMappedReasonCodes={contactReviewHardCodes}
            softReasonCodes={contactReviewSoftCodes}
            isAllComplete={
              isReadyToSubmit &&
              requiredFieldsComplete &&
              allFieldChecksComplete &&
              !contactValidationMessage
            }
            onMarkEdited={handleMarkEdited}
            onMarkInsufficient={handleMarkInsufficient}
            onSubmitVerification={() => void submitVerification()}
            isSubmitting={isSubmitting}
          />
        </>
      )}

      <Modal
        title={modalTitle}
        showModal={confirmAction !== null}
        setShowModal={() => setConfirmAction(null)}
        variant={modalVariant}
        labelButtonConfirm={modalLabel}
        onConfirm={handleConfirm}
        isLoadingButtonConfirm={isSubmitting}
        showCancelButton
        showModalActions
        text={textWithError || ' '}
      />
    </OfferLetterPanel>
  );
};

export default OfferLetterVerificationPanel;
